import { useCallback, useEffect, useState } from 'react'
import { kickoffWithDate, windowLabel } from '../../shared/clock'
import { api, type DraftGame, type DraftWeek, type SearchGame } from '../api'
import { RemoveButton, useArmed } from './AdminBoard'

/*
 * Add Game.
 *
 * The commissioner is three games short and knows roughly what he wants, so
 * this screen answers one question - "is the game I am thinking of in this
 * week?" - and answers it across both leagues at once. Every other pick'em
 * makes you choose college or NFL first, which is a question about their
 * database rather than about the weekend.
 *
 * FAMILY TEAMS FIRST is the whole reason favourites exist. A board with
 * August's Georgia on it gets picked by August; a board of ten good games he
 * has no stake in does not. The group is not a filter and cannot be turned
 * off - it is the sort order, permanently.
 */

/** The same 44px-hit-area trick the board rows use, for a 36px-tall button. */
const TAP_ROW36 = "relative after:absolute after:inset-x-0 after:-inset-y-1 after:content-['']"
const CARD = 'slate-card flex min-h-[56px] items-center gap-2.5 px-3 py-2.5'

const FILTERS = [
  { key: 'all', label: 'ALL' },
  { key: 'cfb', label: 'CFB' },
  { key: 'top25', label: 'TOP 25' },
  { key: 'nfl', label: 'NFL' },
  { key: 'primetime', label: 'PRIMETIME' },
]

export function AddGame({
  onBack,
  onChanged,
  tz,
}: {
  /**
   * Called when the board FILLS, and only then.
   *
   * Not after every add: the commissioner arrives here needing three games,
   * and bouncing him back to the board after each one makes him retype the
   * search twice. Staying put is also what the approved design assumes - it
   * draws an ON BOARD state for a row on THIS screen, which nobody would ever
   * see if adding a game left the screen.
   */
  onBack?: () => void
  onChanged?: () => void
  tz?: string
}) {
  const [week, setWeek] = useState<DraftWeek | null>(null)
  const [board, setBoard] = useState<DraftGame[]>([])
  const [res, setRes] = useState<{ family: SearchGame[]; rest: SearchGame[] } | null>(null)
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState('all')
  const [note, setNote] = useState<string | null>(null)
  const [armed, arm] = useArmed()
  /** The fixture whose ADD is in flight, so the row can say so. */
  const [busyId, setBusyId] = useState<string | null>(null)
  const [counts, setCounts] = useState<Record<string, number> | null>(null)

  /*
   * The board is loaded here as well as on the admin screen, because this one
   * needs three things from it that a search result cannot carry: the week to
   * search inside, the count in the header, and the id of a game already on
   * the board so the ✕ has something to remove.
   */
  const loadBoard = useCallback(async () => {
    try {
      const d = await api.admin.draft.get()
      setWeek(d.week)
      setBoard(d.games)
      return d
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not load the board.')
      return null
    }
  }, [])

  useEffect(() => {
    void loadBoard()
  }, [loadBoard])

  const weekId = week?.id ?? null

  /*
   * Search on every keystroke, a beat late.
   *
   * The candidate list is a whole weekend of both leagues, so the empty query
   * is a real and useful result - the screen is browsable before it is
   * searchable, and lands showing family teams first without anyone typing.
   */
  useEffect(() => {
    if (!weekId) return
    const t = window.setTimeout(async () => {
      try {
        const r = await api.admin.search(weekId, q, filter)
        setRes(r)
        // Counts do not depend on the chosen filter, so a stale set is
        // better than none while a search is in flight.
        if (r.counts) setCounts(r.counts)
      } catch (e) {
        setNote(e instanceof Error ? e.message : 'Could not search.')
      }
    }, q ? 220 : 0)
    return () => window.clearTimeout(t)
  }, [weekId, q, filter])

  /*
   * BOTH DERIVED FROM `board`, which reloads on every add and remove - so a
   * tap changes the row it was aimed at, immediately.
   *
   * Reading them off the search payload instead looked equivalent and was not:
   * adding a game refreshes the board and NOT the search, so the row went on
   * saying ADD after a successful add and the button felt dead.
   *
   * Keyed on the ESPN id rather than a team name. Matching on the home team
   * read plausibly - a team plays once a week - but across ESPN's ten-day
   * college week 1 BYU hosts twice, both rows resolved to one database id, and
   * the ✕ on the second would have removed the first.
   */
  const boardByEspn = new Map(board.map((g) => [g.espnId, g.id]))
  const usedTeams = new Set(board.flatMap((g) => [g.home.full, g.away.full]))
  const size = week?.board_size ?? 10

  const add = async (g: SearchGame) => {
    if (!weekId || busyId) return
    setNote(null)
    /*
     * The tap has to answer before the network does. Adding a game is two
     * round trips - the add, then the board reload that flips this row to ON
     * BOARD - and on a phone that is long enough to press the button twice.
     */
    setBusyId(g.espnId)
    try {
      await api.admin.draft.add(weekId, g.espnId)
      const d = await loadBoard()
      onChanged?.()
      if (d?.week && d.games.length >= (d.week.board_size ?? 10)) onBack?.()
    } catch (e) {
      // 409s here are readable and specific ("one of those teams is already
      // on the board"), which is more use than anything invented locally.
      setNote(e instanceof Error ? e.message : 'Could not add that game.')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (gameId: string) => {
    arm(null)
    try {
      await api.admin.draft.remove(gameId)
      await loadBoard()
      onChanged?.()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not remove that game.')
    }
  }

  const row = (g: SearchGame) => {
    const gameId = boardByEspn.get(g.espnId) ?? null
    // Not on the board, but a team of it is - and a board takes each team once.
    const blockedBy = gameId
      ? null
      : ([g.home.full, g.away.full].find((t) => usedTeams.has(t)) ?? null)
    const adding = busyId === g.espnId
    const name = `${g.away.name} AT ${g.home.name}`.toUpperCase()

    return (
      <li key={g.espnId} className={[CARD, gameId ? 'opacity-55' : ''].join(' ')}>
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[17px] leading-none font-bold tracking-[0.02em] text-ink uppercase">
              {name}
            </span>
            {/*
             * Gold, and the only gold on this screen: a starred team is
             * something a PLAYER did. The game's own facts - league, kickoff,
             * network, ranks - stay mute on the line below.
             */}
            {g.fans.length > 0 && (
              <span className="slate-num rounded-[2px] bg-gold/18 px-[5px] py-[2px] text-[8.5px] font-medium tracking-[0.06em] whitespace-nowrap text-gold-text">
                &#9733; {fanLabel(g.fans)}
              </span>
            )}
          </span>
          <span className="slate-num text-[9px] tracking-[0.06em] text-mute">{meta(g, tz)}</span>
        </div>

        {gameId ? (
          <span className="flex shrink-0 items-center gap-2">
            <span className="slate-num text-[10px] font-semibold tracking-[0.08em] text-mute">
              ON BOARD
            </span>
            <RemoveButton
              armed={armed === g.espnId}
              what={name}
              onArm={() => arm(g.espnId)}
              onRemove={() => void remove(gameId)}
            />
          </span>
        ) : blockedBy ? (
          /* Not on the board - but one of its teams is, and a board takes each
             team once. Naming the team makes it a rule rather than a dead end. */
          <span className="slate-num shrink-0 text-right text-[9px] leading-[1.4] tracking-[0.06em] text-faint uppercase">
            {blockedBy}
            <br />
            already on
          </span>
        ) : (
          <button
            type="button"
            disabled={adding}
            onClick={() => void add(g)}
            aria-label={`Add ${name}`}
            className={[
              'slate-num inline-flex min-h-[36px] shrink-0 items-center rounded-[3px] px-3.5 text-[11px] font-bold tracking-[0.08em]',
              TAP_ROW36,
              // Held in gold while it works: the row is about to become one of
              // yours, and gold is the colour for a thing you did.
              adding ? 'bg-gold text-nightink' : 'bg-ink text-ground',
            ].join(' ')}
          >
            {adding ? 'ADDING…' : '+ ADD'}
          </button>
        )}
      </li>
    )
  }

  return (
    <div className="pb-5">
      <header className="flex items-baseline justify-between px-[18px] pt-5 pb-3">
        <span className="text-[30px] font-extrabold tracking-[0.03em] uppercase">
          Add game
        </span>
        <span className="slate-num text-[10.5px] font-medium tracking-[0.1em] text-mute">
          {week ? `WEEK ${week.number} · ${board.length} OF ${size}` : 'NO DRAFT'}
        </span>
      </header>

      {/*
       * The ink border is the point: this is the only thing on the screen that
       * wants a thumb before anything else does, so it is drawn at the weight
       * every other action on the board is drawn at.
       */}
      <div className="mx-3 mb-2.5 flex min-h-[50px] items-center gap-2.5 rounded-[6px] border-[1.5px] border-ink bg-card px-3.5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search teams"
          autoComplete="off"
          /* Stretched, so the tap target is the whole 50px box rather than
             the 21px line of text sitting in the middle of it. */
          className="slate-num min-w-0 flex-1 self-stretch bg-card text-[14px] font-semibold text-ink outline-none"
        />
        <span className="slate-num shrink-0 text-[10px] text-faint">SEARCH TEAMS</span>
      </div>

      <div className="flex flex-wrap gap-2 px-3 pb-3">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            aria-pressed={filter === f.key}
            className={[
              'slate-num',
              TAP_ROW36,
              // 38, not 36: the row stretches every chip to the height of a
              // bordered one, so the filled one is 38 in the design too.
              'inline-flex min-h-[38px] items-center rounded-[3px] px-3 text-[10px] font-semibold tracking-[0.1em]',
              filter === f.key ? 'bg-ink text-ground' : 'border border-hairline bg-card text-slate',
            ].join(' ')}
          >
            {f.label}
            {counts && (
              <span className="ml-1.5 opacity-60">{counts[f.key] ?? 0}</span>
            )}
          </button>
        ))}
      </div>

      {/*
       * Under the filters, not under the list.
       *
       * It answers "which games could be here at all", and drawn at the foot
       * of a scrolling list it was never on screen at the moment that question
       * gets asked - which is while looking at the results and wondering where
       * a game went.
       */}
      {week && (
        <p className="slate-num px-[18px] pb-3 text-[9px] leading-[1.6] tracking-[0.06em] text-faint">
          {week.label.toUpperCase()} &mdash; {windowLabel(week.opens_at, week.closes_at, tz)}
          {' · '}KICKOFFS OUTSIDE THESE DAYS ARE NOT LISTED
        </p>
      )}

      <ul className="flex flex-col gap-2 px-3 pb-3.5">
        {res && res.family.length > 0 && <Group>Family teams first</Group>}
        {res?.family.map(row)}
        {res && res.rest.length > 0 && <Group>Everything else</Group>}
        {res?.rest.map(row)}
      </ul>

      {res && res.family.length + res.rest.length === 0 && (
        <p className="slate-num px-[18px] pb-3 text-[9px] tracking-[0.06em] text-mute">
          NOTHING IN THIS WEEK MATCHES THAT.
        </p>
      )}

      {note && (
        <p className="slate-num px-[18px] text-[9px] leading-[1.6] tracking-[0.06em] text-mute">
          {note}
        </p>
      )}
    </div>
  )
}

function Group({ children }: { children: string }) {
  return (
    <li className="slate-num px-1.5 pt-1 text-[8.5px] font-medium tracking-[0.12em] text-faint uppercase">
      {children}
    </li>
  )
}

/**
 * Who starred it: one name possessive, more than one name plus a count.
 *
 * "GRANDPA +2" rather than three names, because three names at 8.5px beside a
 * 17px matchup wraps the row and the row is the thing being scanned.
 */
function fanLabel(fans: string[]): string {
  const first = (fans[0] ?? '').toUpperCase()
  return fans.length === 1 ? `${first}’S TEAM` : `${first} +${fans.length - 1}`
}

/**
 * CFB · SAT SEP 5, 6:00 PM MDT · CBS · RK 3 AT RK 5
 *
 * WITH THE DATE, and it is not decoration. A board window runs Thursday to
 * Monday, and ESPN's college week 1 spans ten days - so this list routinely
 * holds two different Saturdays, and a pinned team appears on both. Reading
 * "SAT" against "SAT" made two separate fixtures look like one duplicated
 * row, which is exactly how it was reported.
 */
function meta(g: SearchGame, tz?: string): string {
  const ranked = g.away.rank !== null || g.home.rank !== null
  const rk = (n: number | null) => (n === null ? '—' : `RK ${n}`)
  return [
    g.league === 'nfl' ? 'NFL' : 'CFB',
    kickoffWithDate(g.kickoffAt, tz),
    g.networks[0]?.toUpperCase(),
    ranked ? `${rk(g.away.rank)} AT ${rk(g.home.rank)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

